import type { PlanGatedQuizType } from '@/shared/plan/consts';

/**
 * 빌드 화면의 **유형 공용** 분석 이벤트 카탈로그.
 *
 * 규약은 `docs/event-tracking/guide.md`. 이름은 `quiz_<area>_<target>_<action>`,
 * prop 은 flat scalar 만(OTel 제약), 글로벌 자동주입 값(user/space/device/plan/locale)은
 * 다시 보내지 않는다. 발화는 `@zep/logger/react` 의 `useTrackEvent` 경유.
 *
 * 여기 있는 이벤트는 문항 유형을 가리지 않는다 — 유형 선택은 어떤 유형을 골랐든 한 번,
 * 저장은 어떤 유형이 섞여 있든 **저장 1회당 한 번** 발화한다. 그래서 유형별 카탈로그
 * (`events.quiz-match.ts` · `events.quiz-order.ts`)가 아니라 이 파일이 소유한다.
 *
 * 새 유형이 늘면 여기를 넓힌다 — `question_type` 유니온에 추가하고, 저장 계열에
 * `has_<type>_question` / `<type>_question_count` 쌍을 더한다. 유형별 카탈로그에 같은 키를
 * 다시 선언하면 안 된다(`interface` 병합 충돌).
 */
export type QuizBuildType = 'create' | 'update' | 'duplicate';

/**
 * 이 빌드를 **어느 CTA 로 들어와 시작했는지**. `setReferrer` 가 남긴 진입 식별자를 싣는다.
 *
 * 출발 「페이지」가 아니라 **진입점**이다 — `play_jelly_copy_guide`·`gnb_copy` 처럼
 * 같은 페이지 안의 서로 다른 버튼을 가른다.
 *
 * `build_type: 'duplicate'` 만으로는 체험 퀴즈 CTA·젤리 잠금 안내·공개 퀴즈 배너에서 온
 * 복제가 한 덩어리로 섞여, 각 유입의 **완주율**을 가를 수 없다. 이 값이 그 경계다.
 *
 * 값 목록을 유니온으로 좁히지 않는다 — 진입점은 계속 늘고, 여기서 좁히면 새 입구가
 * 생길 때마다 이 파일이 병목이 된다(`TrackingLocation` 도 같은 이유로 string 이다).
 * 진입 기록이 없거나 만료(5분)면 `unknown` 이 들어간다 — 빈 문자열로 두면 「값이 없는
 * 것」과 「진입점을 모르는 것」이 구분되지 않는다.
 */
export const UNKNOWN_BUILD_ENTRY_POINT = 'unknown';

declare module '@zep/logger' {
  interface EventMap {
    quiz_build_question_type_select: {
      question_type: PlanGatedQuizType;
      place: 'question_card';
      build_type: QuizBuildType;
    };
    quiz_build_question_save_attempt: {
      save_attempt_id: string;
      build_type: QuizBuildType;
      build_entry_point: string;
      has_match_question: boolean;
      match_question_count: number;
      has_order_question: boolean;
      order_question_count: number;
    };
    quiz_build_question_save: {
      save_attempt_id: string;
      build_type: QuizBuildType;
      build_entry_point: string;
      result: 'success' | 'validation_fail' | 'api_fail';
      fail_reason?: string;
      has_match_question: boolean;
      match_question_count: number;
      has_order_question: boolean;
      order_question_count: number;
    };
  }
}

export {};
